import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";
import { HeaderProps } from "@lib/header";
import PhoneLink from "@components/utils/phone-link";
import * as gtag from "@lib/utilities/analytics";
import { IconShield } from "@components/utils/icons";

/**
 * The header, per the approved direction (D40 and the header-row rule):
 *
 *   brand | nav | actions — one row that NEVER wraps. The nav is the only
 *   flexible part; below lg it collapses behind a burger. Language, ΕΟΠΥΥ and
 *   the phone stay on the first line at every width, because the phone is the
 *   primary conversion and a stacked header pushes the hero below the fold.
 *
 * Logo: symbol + handwritten wordmark at matched heights, wordmark in the
 * slide-5 light violet. The symbol sits on a white plate — its inner arc is
 * violet-700 and disappears on any tint. The full lockup is NOT used here: at
 * nav height its practitioner and service lines are unreadable noise.
 *
 * ΕΟΠΥΥ is a LINKED badge (D37 — stays on top): it answers the most-asked
 * phone question, and its click is a tracked GA4 event that measures how
 * insurance-led the traffic actually is.
 *
 * Sticky, compacting slightly on scroll.
 */
export default function Header(props: HeaderProps & {
  showBookAppointmentModal?: () => void;
  onOpenSearch?: () => void;
}) {
  // the PSDs draw NO line under the header at rest; the rule returns only
  // once the sticky header floats over content (owner, 2026-09-14)
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const router = useRouter();
  const { t } = useTranslation("main");

  useEffect(() => {
    const onScroll = () => setCompact(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close the drawer on navigation
  useEffect(() => {
    const close = () => setOpen(false);
    router.events.on("routeChangeComplete", close);
    return () => router.events.off("routeChangeComplete", close);
  }, [router.events]);

  const items = props.menu?.items ?? [];
  // driven by the configured locale list, so the switcher disappears while en
  // is disabled (2026-09-09) and comes back with it — no header edit needed
  const locales = router.locales ?? [];

  return (
    <header className={`sticky top-0 z-40 border-b bg-white/95 backdrop-blur-sm transition-colors ${
      scrolled ? "border-rule" : "border-transparent"}`}>
      {/* NOT .shell: the row (brand + 6 nav items + language + ΕΟΠΥΥ + phone)
          measures ~1270px, and the 1140px shell would force the nav to overlap
          the brand — verified by rendering. The header alone gets a wider
          container; the page content stays boxed. */}
      <div className="mx-auto flex max-w-[90rem] items-center justify-between gap-2 px-3 py-3 sm:gap-4 sm:px-4 md:px-8">
        {/* brand */}
        {/* prefetch={false} on ALL chrome links (header/footer/banner): the
            logo alone was prefetching the homepage's 217KB data JSON into the
            LCP bandwidth window on every page (P10 Lighthouse). Click-time
            fetches are ~100ms — invisible next to the render. */}
        <Link href="/" prefetch={false} className="flex flex-none items-center" aria-label={t('header.homeAria')}>
          {/* the official typeset lockup (art-director kit, 2026-09-08;
              supersedes D40's symbol+script pair) — SVG, so it stays crisp
              at every DPI and needs no plate */}
          <Image src="/images/brand/logo.svg" alt="Physiowave Therapy"
            width={437} height={133} priority
            className={`w-auto transition-all ${compact ? "h-10 sm:h-11" : "h-11 sm:h-[3.375rem]"}`} />
        </Link>

        {/* nav — the only flexible part */}
        <nav className="hidden min-w-0 flex-1 items-center justify-center gap-3 min-[1400px]:gap-5 xl:flex" aria-label={t('header.mainNavAria')}>
          {items.map((item, i) => (
            <Link key={i} href={item.url ?? "#"} prefetch={false}
              className={`whitespace-nowrap text-sm min-[1400px]:text-base font-bold transition-colors hover:text-violet-700 ${
                router.asPath === item.url ? "text-violet-700" : "text-body"
              }`}>
              {item.text}
            </Link>
          ))}
        </nav>

        {/* actions — never wraps */}
        <div className="flex flex-none items-center gap-1.5 sm:gap-3">
          {locales.length > 1 ? (
            <span className="inline-flex overflow-hidden rounded-full border border-violet-200 bg-white" role="group" aria-label={t('header.languageAria')}>
              {locales.map((loc) => (
                <Link key={loc} href={router.asPath} locale={loc} lang={loc} prefetch={false}
                  aria-current={router.locale === loc ? "true" : undefined}
                  className={`px-2.5 py-1 text-xs font-extrabold transition-colors ${
                    router.locale === loc ? "bg-violet-700 text-white" : "text-violet-700 hover:bg-violet-050"
                  }`}>
                  {loc.toUpperCase()}
                </Link>
              ))}
            </span>
          ) : null}

          <button type="button" onClick={props.onOpenSearch}
            aria-label={t("header.search")}
            className="grid h-10 w-10 flex-none cursor-pointer place-items-center rounded-full border border-violet-200 text-violet-700 transition-colors hover:bg-violet-050">
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m20.5 20.5-4.6-4.6" />
            </svg>
          </button>

          <Link href="/synedries-eopyy" prefetch={false}
            onClick={() => gtag.event("eopyy_click", { event_category: "engagement", event_label: "header" })}
            className="hidden items-center gap-1.5 rounded-md border border-violet-200 bg-violet-050 px-3 py-2 text-[0.8125rem] font-extrabold tracking-wide text-violet-700 transition-colors hover:border-violet-700 hover:bg-violet-200 sm:inline-flex">
            <IconShield width={15} height={15} className="flex-none" />
            {t("header.eopyyBadge")}
          </Link>

          <span className="hidden sm:inline-flex"><PhoneLink variant="header" placement="header" /></span>

          <button type="button" onClick={() => setOpen(!open)}
            aria-expanded={open} aria-label={open ? t('header.menuCloseAria') : t('header.menuOpenAria')}
            className="grid h-10 w-10 place-items-center rounded-lg border border-violet-200 text-violet-700 xl:hidden">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
                 strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              {open ? <path d="m6 6 12 12M18 6 6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>

      {/* mobile drawer */}
      {open ? (
        <nav className="border-t border-rule bg-white xl:hidden" aria-label={t('header.mainNavAria')}>
          <div className="shell flex flex-col gap-1 py-4">
            {items.map((item, i) => (
              <Link key={i} href={item.url ?? "#"} prefetch={false}
                className="rounded-lg px-3 py-2.5 font-bold text-body hover:bg-violet-050 hover:text-violet-700">
                {item.text}
              </Link>
            ))}
            <button type="button"
              onClick={() => { setOpen(false); props.onOpenSearch?.(); }}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left font-bold text-body hover:bg-violet-050 hover:text-violet-700">
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" /><path d="m20.5 20.5-4.6-4.6" />
              </svg>
              {t("header.search")}
            </button>
            <div className="mt-3 flex items-center gap-3 border-t border-rule px-3 pt-4">
              <PhoneLink variant="header" placement="mobile-menu" />
              <Link href="/synedries-eopyy" prefetch={false} className="text-sm font-extrabold text-violet-700">{t("header.eopyyBadge")}</Link>
            </div>
          </div>
        </nav>
      ) : null}
    </header>
  );
}
